import { useEffect, useRef, useState } from "react";
import { NAVY, GOLD, DANGER } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { FleetMap } from "./FleetMap";
import { AllRoutesMap } from "./AllRoutesMap";
import { TripRouteMap } from "./TripRouteMap";
import { ClickableDate } from "./ClickableDate";
import { useComms } from "../lib/commsReady";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";

// Local YYYY-MM-DD — avoids the toISOString() UTC skew that shifts evening dates a day.
const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"}) : "";
const svcLabelShort = (t) => t === "deo" ? "deodorizer" : "cleanup";

export function FleetPage({ technicians, dbVehicles, showToast, onRefreshVehicles }) {
  const { fleetReady, guideFleet } = useComms();
  const isMobile = useIsMobile();
  const [vehicles,     setVehicles]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const [selectedVeh,  setSelectedVeh]  = useState(null);
  const [liveTrips,    setLiveTrips]    = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const intervalRef     = useRef(null);
  const gpsJobTracker   = useRef({});
  // Trip tracking state
  const [dbTrips,      setDbTrips]      = useState([]);
  const [tripsTab,     setTripsTab]     = useState("live"); // "live" or "mileage"
  // eslint-disable-next-line no-unused-vars
  const [syncing,      setSyncing]      = useState(false);
  const [dateFrom,     setDateFrom]     = useState(ymd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo,       setDateTo]       = useState(ymd(new Date()));
  const [irsRate,      setIrsRate]      = useState(0.70);
  const [selectedTrip, setSelectedTrip] = useState(null);
  // iOS ignores body overflow:hidden — use the theme's body.sheet-open lock while the trip sheet is up on phones.
  const lockBody = isMobile && !!selectedTrip;
  useEffect(() => {
    if (!lockBody) return undefined;
    const y = window.scrollY;
    document.body.classList.add("sheet-open");
    document.body.style.top = `-${y}px`;
    return () => { document.body.classList.remove("sheet-open"); document.body.style.top = ""; window.scrollTo(0, y); };
  }, [lockBody]);
  const [tripRoute,    setTripRoute]    = useState(null); // geojson coords for selected trip
  const [showAllRoutes,setShowAllRoutes]= useState(false);
  const [allRoutes,    setAllRoutes]    = useState([]); // [{trip, route}]
  const [loadingRoutes,setLoadingRoutes]= useState(false);
  // GPS-detected job suggestions (assistive auto start/complete)
  const [suggestions,  setSuggestions]  = useState([]);
  const [dismissed,    setDismissed]    = useState(new Set());
  const [suggBusy,     setSuggBusy]     = useState(null);

  function closeTrip() { setSelectedTrip(null); setTripRoute(null); }
  // Phone: the trip route opens as a bottom sheet — let the iOS back gesture close it.
  useBackClose(!!selectedTrip && isMobile, closeTrip);

  async function checkGpsJobTracking(freshVehicles) {
    // For each vehicle with a tech in auto mode, check position against today's jobs.
    // Local date — toISOString() is UTC, so from ~8pm ET it looked at TOMORROW's jobs
    // and auto start/complete never matched on evening checks.
    const today = ymd(new Date());
    const allVehicles = await fetch(`${SUPABASE_URL}/rest/v1/vehicles?technician_id=not.is.null&select=id,imei,technician_id,technician_name`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json());
    if (!Array.isArray(allVehicles)) return;

    for (const veh of allVehicles) {
      // Get tech settings
      const tech = await fetch(`${SUPABASE_URL}/rest/v1/technicians?id=eq.${veh.technician_id}&select=id,name,email,job_completion_mode,default_visit_note,email_notifications,home_lat,home_lng&limit=1`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json()).then((rows:any[])=>rows[0]);
      if (!tech || tech.job_completion_mode !== "auto") continue;

      // Get today's jobs for this tech
      const jobs = await fetch(`${SUPABASE_URL}/rest/v1/jobs?technician_id=eq.${veh.technician_id}&scheduled_date=eq.${today}&status=in.(scheduled,started)&select=id,client_id,client_name,client_address,status`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json());
      if (!Array.isArray(jobs)||!jobs.length) continue;

      // Get client coords
      const clientIds = jobs.map((j)=>j.client_id).join(",");
      const clients = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=in.(${clientIds})&select=id,name,lat,lng`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json());

      // Get vehicle current position
      const liveVeh = freshVehicles.find((v)=>v.imei===veh.imei);
      if (!liveVeh?.stats?.location?.lat) continue;
      const vLat = Number(liveVeh.stats.location.lat);
      const vLon = Number(liveVeh.stats.location.lon);
      const tracker = gpsJobTracker.current;
      const key = veh.imei;
      if (!tracker[key]) tracker[key] = { arrivedAt:null, jobId:null };

      // Skip GPS tracking if tech is at home
      const atHome = tech.home_lat&&tech.home_lng
        ? haversineMeters(vLat, vLon, Number(tech.home_lat), Number(tech.home_lng)) <= 91.44
        : false;
      if (atHome) continue;

      // Check distance to each scheduled job's client
      let nearbyJob = null;
      for (const job of jobs) {
        if (job.status !== "scheduled") continue;
        const client = clients.find((c)=>c.id===job.client_id);
        if (!client?.lat) continue;
        const dist = haversineMeters(vLat, vLon, Number(client.lat), Number(client.lng));
        if (dist <= 91.44) { // 300ft in meters
          nearbyJob = job;
          break;
        }
      }

      // Auto-start: arrived near a scheduled job
      if (nearbyJob && tracker[key].jobId !== nearbyJob.id) {
        tracker[key] = { arrivedAt: Date.now(), jobId: nearbyJob.id };
        // Mark job started
        await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${nearbyJob.id}`,{
          method:"PATCH",
          headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
          body:JSON.stringify({status:"started",started_at:new Date().toISOString()})
        });
        // Send email notification
        if (tech.email_notifications && tech.email) {
          await fetch(`${SUPABASE_URL}/functions/v1/send-job-notification`,{
            method:"POST",
            headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
            body:JSON.stringify({ to:tech.email, tech_name:tech.name, client_name:nearbyJob.client_name, type:"started", address:nearbyJob.client_address })
          }).catch(()=>{});
        }
        console.log(`Auto-started job for ${tech.name} at ${nearbyJob.client_name}`);
      }

      // Auto-complete: was near a started job, now moved away for 2+ minutes
      const startedJob = jobs.find((j)=>j.status==="started");
      if (startedJob) {
        const startedClient = clients.find((c)=>c.id===startedJob.client_id);
        if (startedClient?.lat) {
          const dist = haversineMeters(vLat, vLon, Number(startedClient.lat), Number(startedClient.lng));
          if (dist > 91.44) {
            // Moved away — check if they've been away for 2+ minutes
            if (!tracker[key].arrivedAt) tracker[key].arrivedAt = Date.now();
            const awayMs = Date.now() - tracker[key].arrivedAt;
            if (awayMs >= 120000) { // 2 minutes
              tracker[key] = { arrivedAt:null, jobId:null };
              const note = tech.default_visit_note || "";
              await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${startedJob.id}`,{
                method:"PATCH",
                headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
                body:JSON.stringify({status:"completed",completed_at:new Date().toISOString(),visit_notes:note||null})
              });
              if (tech.email_notifications && tech.email) {
                await fetch(`${SUPABASE_URL}/functions/v1/send-job-notification`,{
                  method:"POST",
                  headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                  body:JSON.stringify({ to:tech.email, tech_name:tech.name, client_name:startedJob.client_name, type:"completed", address:startedJob.client_address })
                }).catch(()=>{});
              }
              console.log(`Auto-completed job for ${tech.name} at ${startedJob.client_name}`);
            }
          } else {
            // Still nearby — reset away timer
            tracker[key].arrivedAt = Date.now();
          }
        }
      }
    }
  }

  function haversineMeters(lat1:number,lon1:number,lat2:number,lon2:number):number {
    const R=6371000,dLat=(lat2-lat1)*Math.PI/180,dLon=(lon2-lon1)*Math.PI/180;
    const a=Math.sin(dLat/2)**2+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
    return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
  }

  async function fetchVehicles() {
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/bouncie-proxy?endpoint=/vehicles`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` }
      }).then(r => r.json());
      if (Array.isArray(res)) {
        setVehicles(res);
        setLastRefresh(new Date());
        // Check GPS job tracking for auto-mode techs
        checkGpsJobTracking(res).catch(e=>console.error("GPS tracking error:",e));
      } else if (res.error) {
        showToast("Bouncie error: " + res.error, "error");
      }
    } catch(e) { showToast("Failed to connect to Bouncie", "error"); }
    setLoading(false);
  }

  async function fetchLiveTrips(imei) {
    setTripsLoading(true);
    try {
      const end   = new Date();
      const start = new Date(); start.setDate(start.getDate() - 7);
      const res = await fetch(`${SUPABASE_URL}/functions/v1/bouncie-proxy?endpoint=${encodeURIComponent(`/trips?imei=${imei}&starts-after=${start.toISOString()}&ends-before=${end.toISOString()}&gpsFormat=polyline`)}`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` }
      }).then(r => r.json());
      // Sort NEWEST-first — Bouncie returns trips oldest-first, so the old slice(0,20)
      // showed the oldest 20 of the 7-day window (today's were cut off → "not updated").
      if (Array.isArray(res)) setLiveTrips(res.slice().sort((a,b)=>new Date(b.startTime).getTime()-new Date(a.startTime).getTime()));
    } catch(e) { console.error("Trips error:", e); }
    setTripsLoading(false);
  }

  async function fetchAllTripRoutes(trips) {
    setLoadingRoutes(true);
    setShowAllRoutes(true);
    // All routes come from DB — instant, no API calls needed
    const results = trips.map(trip=>({
      trip,
      route: trip.route_geojson?.coordinates || null
    }));
    setAllRoutes(results);
    setLoadingRoutes(false);
  }

  async function fetchTripRoute(trip) {
    if (selectedTrip?.id === trip.id) { closeTrip(); return; }
    setSelectedTrip(trip);
    // Load route from DB — source of truth
    if (trip.route_geojson?.coordinates) {
      setTripRoute(trip.route_geojson.coordinates);
    } else {
      setTripRoute(null);
    }
  }

  async function loadDbTrips() {
    const from = new Date(dateFrom+"T00:00:00").toISOString();   // local midnight (match the `to` boundary, no UTC skew)
    const to   = new Date(dateTo+"T23:59:59").toISOString();
    const rows = await fetch(`${SUPABASE_URL}/rest/v1/trips?start_time=gte.${from}&start_time=lte.${to}&order=start_time.desc`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json());
    if (Array.isArray(rows)) setDbTrips(rows);
  }

  // ── GPS suggestions (assistive auto start/complete) ───────────────
  async function loadSuggestions() {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/bouncie-suggestions`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` }
      }).then(r => r.json());
      if (Array.isArray(res?.suggestions)) setSuggestions(res.suggestions);
    } catch (e) { /* non-fatal — suggestions are best-effort */ }
  }
  async function rpcJobTransition(serviceId, action, payload) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/job_transition`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
      body: JSON.stringify({ p_service_id: serviceId, p_action: action, p_payload: payload || {} }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || e.error || e.hint || `HTTP ${res.status}`); }
  }
  async function confirmSuggestion(s) {
    const key = s.header_id + s.kind;   // stable per header+kind (matches the server's dedup)
    setSuggBusy(key);
    try {
      if (s.kind === "arrival") {
        if (!s.service_ids?.length) throw new Error("no scheduled service to start");
        await rpcJobTransition(s.service_ids[0], "start", {});        // one call starts all scheduled services on the stop
      } else {
        // Complete each started service; tolerate ones already completed (e.g. a re-click)
        // so a partial state is resumable rather than stuck on the first already-done id.
        let done = 0, failed = 0;
        for (const sid of (s.service_ids || [])) {
          try { await rpcJobTransition(sid, "complete_service", {}); done++; }
          catch (e) {
            if (/completable state/i.test(e.message || "")) continue; // already done — not a failure
            failed++;
          }
        }
        if (failed) throw new Error(`completed ${done} of ${s.service_ids.length} — retry to finish`);
      }
      showToast(s.kind === "arrival" ? `Started ${s.client_name}'s job` : `Completed ${s.client_name}'s job`);
      setDismissed(d => new Set(d).add(key));
    } catch (e) {
      showToast("Couldn't apply: " + (e.message || e), "error");
    } finally {
      setSuggBusy(null);
      loadSuggestions();   // reflect reality (advanced statuses / partial completes)
    }
  }

  // eslint-disable-next-line no-unused-vars
  async function syncTrips() {
    setSyncing(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/sync-bouncie-trips`,{
        method:"POST",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
        body:JSON.stringify({ start_date:"2026-05-01T00:00:00.000Z", end_date:new Date().toISOString() })
      }).then(r=>r.json());
      if (res.error) { showToast("Sync error: "+res.error,"error"); }
      else { showToast(res.message); await loadDbTrips(); }
    } catch(e) { showToast("Sync failed","error"); }
    setSyncing(false);
  }

  async function toggleTripType(trip) {
    const newType  = trip.trip_type==="business"?"personal":"business";
    const irsRate  = 0.70; // use stored rate
    const reimb    = newType==="business" ? Math.round(Number(trip.distance_miles)*irsRate*100)/100 : 0;
    // Clear match if switching to personal
    const updates = {
      trip_type:            newType,
      override:             true,
      reimbursement_amount: reimb,
      synced_at:            new Date().toISOString(),
    };
    if (newType==="personal") {
      updates.matched_client_id   = null;
      updates.matched_client_name = null;
    }
    await fetch(`${SUPABASE_URL}/rest/v1/trips?id=eq.${trip.id}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify(updates)
    });
    setDbTrips(prev=>prev.map(t=>t.id===trip.id?{...t,...updates}:t));
  }

  useEffect(() => {
    if (!fleetReady) return undefined; // not connected → the guide renders instead; don't poll Bouncie
    fetchVehicles();
    loadSuggestions();
    // Auto-refresh every 15 seconds
    intervalRef.current = setInterval(fetchVehicles, 15000);
    return () => clearInterval(intervalRef.current);
  }, [fleetReady]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedVeh) return;
    fetchLiveTrips(selectedVeh.imei);
    // Keep the selected vehicle's trip history live (was load-once on select → went stale).
    const id = setInterval(() => fetchLiveTrips(selectedVeh.imei), 15000);
    return () => clearInterval(id);
  }, [selectedVeh?.imei]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { 
    setIrsRate(0.70); 
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => { 
    if (tripsTab === "mileage") loadDbTrips(); 
    closeTrip();   // the trip sheet/map belongs to the mileage tab — never carry it (or its back-handler) across tabs
  }, [tripsTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line no-unused-vars
  function mileToKm(miles) { return (miles * 1.60934).toFixed(1); }
  function fmtOdo(v) {
    const od = v.stats?.odometer;
    if (!od) return "—";
    return `${Math.round(od).toLocaleString()} mi`;
  }
  function fmtCoords(v) {
    const lat = v.stats?.location?.lat;
    const lon = v.stats?.location?.lon;
    if (!lat || !lon) return "—";
    return `${Number(lat).toFixed(4)}, ${Number(lon).toFixed(4)}`;
  }
  function fmtAddr(v) {
    return v.stats?.location?.address || fmtCoords(v) || "Location unavailable";
  }

  const runningCount = vehicles.filter(v => v.stats?.isRunning || v.stats?.speed > 0).length;
  const parkedCount  = vehicles.length - runningCount;

  // Fleet Map — always mounted (hidden, not unmounted, without fixes) so the map instance
  // persists between the 15s refreshes. Rendered above the vehicle cards on phones, below on desktop.
  const mapCard = (
    <div className="card" style={{marginBottom:isMobile?16:20,display:vehicles.filter(v=>v.stats?.location?.lat).length>0?"block":"none"}}>
      <div className="card-title">📍 Live Fleet Map</div>
      <FleetMap vehicles={vehicles}/>
    </div>
  );

  if (!fleetReady) {
    return (
      <div className="card" style={{ padding: 28, textAlign: "center", maxWidth: 460, margin: "40px auto" }}>
        <div style={{ fontSize: 34 }} aria-hidden="true">🚚</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text)", marginTop: 8 }}>Connect your fleet tracker</div>
        <div style={{ fontSize: 13.5, color: "var(--text2)", marginTop: 8, lineHeight: 1.5 }}>
          Fleet shows live vehicle locations, mileage, and proof-of-service — once you connect Bouncie. It takes about a minute.
        </div>
        <button className="btn btn-gold" style={{ marginTop: 18 }} onClick={guideFleet}>Set up Fleet →</button>
      </div>
    );
  }

  return (
    <div style={{maxWidth:1160,margin:"0 auto"}}>
      <style>{`
        .fleet-card{transition:transform .12s ease,box-shadow .12s ease,border-color .12s ease;}
        @media (hover:hover){.fleet-card:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(7,44,118,.10);}}
        .fleet-veh.sel{border-color:${NAVY}!important;box-shadow:0 0 0 3px rgba(7,44,118,.12);}
        .live-dot{width:8px;height:8px;border-radius:50%;display:inline-block;background:#cbd5e1;flex-shrink:0;}
        .live-dot.on{background:#16a34a;animation:fleetpulse 1.6s infinite;}
        @keyframes fleetpulse{0%{box-shadow:0 0 0 0 rgba(22,163,74,.5)}70%{box-shadow:0 0 0 7px rgba(22,163,74,0)}100%{box-shadow:0 0 0 0 rgba(22,163,74,0)}}
        .fleet-trow{transition:background .1s ease;}
        @media (hover:hover){.fleet-trow:hover td{background:#f7f8fa;}}
        .fleet-trip-mobile{padding:12px 0;border-top:1px solid var(--border);}
        .fleet-trip-mobile:first-child{border-top:none;padding-top:0;}
      `}</style>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:16}}>
        <div>
          <h2 style={{fontFamily:"Syne",fontWeight:800,fontSize:22,color:NAVY,margin:0}}>Fleet</h2>
          <div style={{fontSize:12,color:"#8993a4",marginTop:3}}>
            {lastRefresh ? `Live · updated ${lastRefresh.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit",second:"2-digit"})} · auto-refreshes every 15s` : "Connecting to Bouncie…"}
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={fetchVehicles} disabled={loading}>{loading?"Refreshing…":"↻ Refresh"}</button>
      </div>

      {/* Segmented tabs */}
      {/* Phone: full-width segmented control with short labels — the long ones wrapped into
          two-line buttons at 375px. */}
      <div className="seg" style={{marginBottom:16,...(isMobile?{display:"flex",width:"100%",alignSelf:"stretch"}:{})}}>
        {[["live","🛰 Live tracking","🛰 Live"],["mileage","📊 Mileage & reimbursement","📊 Mileage"]].map(([k,label,short])=>(
          <button key={k} className={tripsTab===k?"on":""} onClick={()=>setTripsTab(k)}
            style={isMobile?{flex:1,justifyContent:"center",whiteSpace:"nowrap",minHeight:40}:undefined}>{isMobile?short:label}</button>
        ))}
      </div>

      {/* GPS-detected job actions (assistive — operator confirms each) */}
      {(()=>{
        const open = suggestions.filter(s => !dismissed.has(s.header_id + s.kind));
        if (!open.length) return null;
        return (
          <div className="card" style={{marginBottom:16,borderLeft:`3px solid ${GOLD}`}}>
            <div className="card-title" style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:10}}>
              🛰 GPS-detected actions
              <span style={{fontSize:11,fontWeight:600,color:"#8993a4"}}>from Bouncie park/depart · confirm to apply</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {open.map(s => {
                const key = s.header_id + s.kind, arr = s.kind === "arrival";
                return (
                  <div key={key} className="fleet-card" style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",padding:"10px 12px",borderRadius:10,background:arr?"#f0fdf4":"#eff6ff",border:"1px solid #e3e8ef"}}>
                    <div style={{flex:1,minWidth:220}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#1a1f36"}}>{arr?"🟢 Arrived":"🏁 Departed"} · {s.client_name}</div>
                      <div style={{fontSize:11,color:"#42526e",marginTop:2}}>
                        {arr
                          ? `Parked ${fmtT(s.arrival_time)} — start ${(s.service_types||[]).map(svcLabelShort).join(" + ")}?`
                          : `Left ${fmtT(s.departure_time)}${s.on_site_mins!=null?` · ${s.on_site_mins}m on site`:""} — complete ${(s.service_types||[]).map(svcLabelShort).join(" + ")}?`}
                      </div>
                    </div>
                    {/* Phone: full-width 44px buttons — these mutate job status, so no 26px targets. */}
                    <div style={{display:"flex",gap:isMobile?8:6,width:isMobile?"100%":undefined}}>
                      <button className={`btn btn-gold${isMobile?"":" btn-sm"}`} style={isMobile?{flex:1,justifyContent:"center",minHeight:44}:undefined} disabled={suggBusy===key} onClick={()=>confirmSuggestion(s)}>
                        {suggBusy===key ? "…" : (arr ? "✓ Start job" : "✓ Complete job")}
                      </button>
                      <button className={`btn btn-ghost${isMobile?"":" btn-sm"}`} style={isMobile?{flex:1,justifyContent:"center",minHeight:44}:undefined} disabled={suggBusy===key} onClick={()=>setDismissed(d=>new Set(d).add(key))}>Dismiss</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {tripsTab==="live"&&<>
      {loading && vehicles.length === 0 && <div className="empty"><p>Connecting to Bouncie…</p></div>}

      {vehicles.length>0 && (
        <div className="card" style={{padding:0,display:isMobile?"grid":"flex",gridTemplateColumns:isMobile?"1fr 1fr":undefined,flexWrap:"wrap",overflow:"hidden",marginBottom:16}}>
          <Stat label="Vehicles" value={vehicles.length} mobile={isMobile} idx={0} />
          <Stat label="Running"  value={runningCount} tone={runningCount>0?"good":undefined} mobile={isMobile} idx={1} />
          <Stat label="Parked"   value={parkedCount} last mobile={isMobile} idx={2} />
        </div>
      )}

      {/* Phone: the live map goes right under the stat bar (it sat 2-3 screens below the
          vehicle cards + trip table). FleetMap stays mounted either way so the map instance persists. */}
      {isMobile && mapCard}

      <div className="grid-2" style={{marginBottom:20}}>
        {vehicles.map(v => {
          const running = v.stats?.isRunning || v.stats?.speed > 0;
          const sel = selectedVeh?.imei===v.imei;
          return (
          <div key={v.imei} className={`card fleet-card fleet-veh${sel?" sel":""}`} style={{cursor:"pointer"}}
            onClick={()=>setSelectedVeh(sel?null:v)}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,gap:10}}>
              <div style={{minWidth:0}}>
                <div style={{fontWeight:700,fontSize:16,color:NAVY,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v.nickName||v.model?.name||"Vehicle"}</div>
                <div style={{fontSize:12,color:"#8993a4"}}>{v.model?.year} {v.model?.make} {v.model?.name}</div>
              </div>
              <span style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:11,fontWeight:700,padding:"4px 10px",borderRadius:999,whiteSpace:"nowrap",
                background:running?"#dcfce7":"#f1f5f9",color:running?"#166534":"#64748b"}}>
                <span className={`live-dot${running?" on":""}`}/>{running?"Running":"Parked"}
              </span>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              {[
                {label:"Odometer", val:fmtOdo(v)},
                {label:"Speed",    val:v.stats?.speed>0?Math.round(v.stats.speed)+" mph":"0 mph"},
                {label:"Fuel",     val:v.stats?.fuelLevel!=null?Math.round(v.stats.fuelLevel)+"%":"—"},
                {label:"Engine",   val:v.standardEngine||"—"},
              ].map(s=>(
                <div key={s.label} style={{background:"#f6f8fa",borderRadius:8,padding:"9px 11px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#8993a4",letterSpacing:".04em"}}>{s.label.toUpperCase()}</div>
                  <div style={{fontWeight:700,fontSize:15,color:NAVY,marginTop:1}}>{s.val}</div>
                </div>
              ))}
            </div>

            <div style={{fontSize:12,color:"#42526e",display:"flex",alignItems:"flex-start",gap:6}}>
              <span>📍</span><span>{fmtAddr(v)}</span>
            </div>
            {v.stats?.location?.lat&&v.stats?.location?.lon&&(
              <a href={`https://maps.google.com/?q=${v.stats.location.lat},${v.stats.location.lon}`} target="_blank" rel="noreferrer"
                className={isMobile?"btn btn-ghost btn-sm":undefined}
                style={isMobile
                  ? {marginTop:10,minHeight:40,padding:"10px 14px",fontSize:13,fontWeight:600,color:NAVY,textDecoration:"none"}
                  : {display:"inline-block",marginTop:8,fontSize:11,fontWeight:600,color:NAVY}}
                onClick={e=>e.stopPropagation()}>
                Open in Google Maps →
              </a>
            )}
            {v.stats?.mil?.milOn===true&&(
              <div style={{marginTop:10,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:8,padding:"6px 10px",fontSize:12,color:DANGER,fontWeight:600}}>⚠️ Check engine light on</div>
            )}
          </div>
          );
        })}
      </div>

      {/* Trip history for selected vehicle */}
      {selectedVeh&&(
        <div className="card">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:14}}>
            <div className="card-title" style={{marginBottom:0}}>Trip History — {selectedVeh.nickName||selectedVeh.model?.name||"Vehicle"} (Last 7 days)</div>
            {tripsLoading&&<span style={{fontSize:12,color:"#8993a4"}}>Loading...</span>}
          </div>
          {liveTrips.length===0&&!tripsLoading&&<div className="empty"><p>No trips in the last 7 days</p></div>}
          {liveTrips.length>0&&(
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Duration</th><th>Distance</th>{!isMobile&&<th>Avg Speed</th>}{!isMobile&&<th>Max Speed</th>}<th>{isMobile?"Braking":"Hard Braking"}</th></tr></thead>
                <tbody>
                  {liveTrips.slice(0,20).map((t,i)=>{
                    const start   = new Date(t.startTime);
                    const end     = new Date(t.endTime);
                    const durMins = Math.round((end-start)/60000);
                    const distMi  = t.distance ? Number(t.distance).toFixed(1) : "—";
                    const dur     = durMins > 0 ? (durMins>=60?Math.floor(durMins/60)+"h "+( durMins%60)+"m":durMins+"m") : "—";
                    const avgSpd  = t.averageSpeed ? Math.round(t.averageSpeed)+" mph" : "—";
                    const maxSpd  = t.maxSpeed ? Math.round(t.maxSpeed)+" mph" : "—";
                    const hardBrk = t.hardBrakingCount ?? 0;
                    return (
                      <tr key={i}>
                        <td style={{fontSize:12,whiteSpace:"nowrap"}}>{start.toLocaleDateString()} {start.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</td>
                        <td>{dur}</td>
                        <td style={{fontWeight:600,whiteSpace:"nowrap"}}>{distMi} mi</td>
                        {!isMobile&&<td>{avgSpd}</td>}
                        {!isMobile&&<td>{maxSpd}</td>}
                        <td style={{color:Number(hardBrk)>0?"#bf2600":"inherit"}}>{hardBrk}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!isMobile && mapCard}

      {vehicles.length===0&&!loading&&(
        <div className="empty">
          <p>No vehicles found. Make sure your Bouncie devices are authorized in the developer portal.</p>
        </div>
      )}
      </> /* end live tab */}

      {/* Mileage Report Tab */}
      {tripsTab==="mileage"&&(
        <div>
          {/* Date range + filters */}
          <div className="card" style={{marginBottom:16}}>
            {/* Quick select buttons */}
            <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
              {[
                {label:"Today",     from:()=>ymd(new Date()), to:()=>ymd(new Date())},
                {label:"Yesterday", from:()=>{ const d=new Date(); d.setDate(d.getDate()-1); return ymd(d); }, to:()=>{ const d=new Date(); d.setDate(d.getDate()-1); return ymd(d); }},
                {label:"This Week", from:()=>{ const d=new Date(); d.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1)); return ymd(d); }, to:()=>ymd(new Date())},
                {label:"This Month",from:()=>ymd(new Date(new Date().getFullYear(),new Date().getMonth(),1)), to:()=>ymd(new Date())},
                {label:"Last Month",from:()=>ymd(new Date(new Date().getFullYear(),new Date().getMonth()-1,1)), to:()=>ymd(new Date(new Date().getFullYear(),new Date().getMonth(),0))},
              ].map(q=>(
                <button key={q.label} className="btn btn-ghost btn-sm" style={isMobile?{fontSize:13,padding:"10px 14px",minHeight:40}:{fontSize:11}}
                  onClick={()=>{
                    const f = q.from();
                    const t = q.to();
                    setDateFrom(f);
                    setDateTo(t);
                    // Load directly with computed values — don't rely on state update timing
                    const from = new Date(f+"T00:00:00").toISOString();   // local midnight (match `to`, no UTC skew)
                    const to   = new Date(t+"T23:59:59").toISOString();
                    fetch(`${SUPABASE_URL}/rest/v1/trips?start_time=gte.${from}&start_time=lte.${to}&order=start_time.desc`,{
                      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
                    }).then(r=>r.json()).then(rows=>{ if(Array.isArray(rows)) setDbTrips(rows); });
                  }}>
                  {q.label}
                </button>
              ))}
            </div>
            {/* Phone: FROM/TO share one row, then the action buttons wrap two-up. */}
            <div style={{display:"flex",gap:isMobile?"10px 8px":12,alignItems:"flex-end",flexWrap:"wrap"}}>
              <div style={isMobile?{flex:"1 1 40%",minWidth:0}:undefined}>
                <div style={{fontSize:11,color:"#8993a4",marginBottom:4}}>FROM</div>
                <ClickableDate value={dateFrom} onChange={setDateFrom} width={isMobile?"100%":undefined} style={isMobile?{boxSizing:"border-box",minHeight:44}:undefined}/>
              </div>
              <div style={isMobile?{flex:"1 1 40%",minWidth:0}:undefined}>
                <div style={{fontSize:11,color:"#8993a4",marginBottom:4}}>TO</div>
                <ClickableDate value={dateTo} onChange={setDateTo} width={isMobile?"100%":undefined} style={isMobile?{boxSizing:"border-box",minHeight:44}:undefined}/>
              </div>
              <button className="btn btn-gold btn-sm" style={isMobile?{flex:"1 1 45%",justifyContent:"center",minHeight:40}:undefined} onClick={loadDbTrips}>Apply</button>
              <button className="btn btn-primary btn-sm" style={isMobile?{flex:"1 1 45%",justifyContent:"center",minHeight:40}:undefined} onClick={()=>{ if(showAllRoutes){setShowAllRoutes(false);setAllRoutes([]);} else fetchAllTripRoutes(dbTrips); }} disabled={loadingRoutes||dbTrips.length===0}>
                {loadingRoutes?"Loading routes...":showAllRoutes?"✕ Hide All Routes":"🗺 View All Routes"}
              </button>
              <button className="btn btn-ghost btn-sm" style={isMobile?{flex:"1 1 45%",justifyContent:"center",minHeight:40}:undefined} onClick={syncTrips} disabled={syncing}>
                {syncing?"Syncing...":"🔄 Sync New Trips"}
              </button>
              <button className="btn btn-ghost btn-sm" style={isMobile?{flex:"1 1 45%",justifyContent:"center",minHeight:40}:undefined} onClick={async()=>{
                if (!window.confirm("Re-match all trips against current client addresses?")) return;
                setSyncing(true);
                try {
                  let offset = 0; let done = false; let totalUpdated = 0;
                  while (!done) {
                    const res = await fetch(`${SUPABASE_URL}/functions/v1/rematch-trips`,{
                      method:"POST",
                      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                      body:JSON.stringify({ offset })
                    }).then(r=>r.json());
                    if (res.error) { showToast("Re-match error: "+res.error,"error"); break; }
                    totalUpdated += res.updated || 0;
                    offset = res.offset;
                    done = res.done;
                  }
                  showToast(`Re-matched ${totalUpdated} trips`);
                  await loadDbTrips();
                } catch(e) { showToast("Re-match failed","error"); }
                setSyncing(false);
              }} disabled={syncing}>
                {syncing?"Working...":"🔀 Re-match Trips"}
              </button>
            </div>
            {/* A dimmed button on a phone doesn't read as disabled — say what's missing. */}
            {isMobile&&dbTrips.length===0&&!loadingRoutes&&(
              <div style={{fontSize:12,color:"var(--text2)",marginTop:8}}>Pick a date range with trips to view routes on the map.</div>
            )}
          </div>

          {/* Summary cards */}
          {(()=>{
            const totalMi   = dbTrips.reduce((s,t)=>s+Number(t.distance_miles||0),0);
            const bizMi     = dbTrips.filter(t=>t.trip_type==="business").reduce((s,t)=>s+Number(t.distance_miles||0),0);
            const persMi    = dbTrips.filter(t=>t.trip_type==="personal").reduce((s,t)=>s+Number(t.distance_miles||0),0);
            const bizValue  = bizMi * irsRate;
            return (
              <div className="card" style={{padding:0,display:isMobile?"grid":"flex",gridTemplateColumns:isMobile?"1fr 1fr":undefined,flexWrap:"wrap",overflow:"hidden",marginBottom:16}}>
                <Stat label="Total miles"    value={totalMi.toFixed(1)} mobile={isMobile} idx={0} />
                <Stat label="Business miles" value={bizMi.toFixed(1)} tone="good" mobile={isMobile} idx={1} />
                <Stat label="Personal miles" value={persMi.toFixed(1)} mobile={isMobile} idx={2} />
                <Stat label="Reimbursement"  value={`$${bizValue.toFixed(2)}`} tone="gold" sub={`@ $${irsRate}/mi IRS rate`} last mobile={isMobile} idx={3} />
              </div>
            );
          })()}

          {/* All routes map */}
          {showAllRoutes&&(
            <div className="card" style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:8}}>
                <div className="card-title" style={{marginBottom:0}}>🗺 All Trip Routes</div>
                {loadingRoutes&&<span style={{fontSize:12,color:"#8993a4"}}>Loading {allRoutes.length}/{dbTrips.length} routes...</span>}
              </div>
              <AllRoutesMap routes={allRoutes} loading={loadingRoutes} matchedClientIds={[...new Set(allRoutes.map(r=>r.trip.matched_client_id).filter(Boolean))]}/>
            </div>
          )}

          {/* Per vehicle breakdown */}
          {dbVehicles.map(v=>{
            const vTrips   = dbTrips.filter(t=>t.imei===v.imei);
            const bizTrips = vTrips.filter(t=>t.trip_type==="business");
            const bizMi    = bizTrips.reduce((s,t)=>s+Number(t.distance_miles||0),0);
            const persMi   = vTrips.filter(t=>t.trip_type==="personal").reduce((s,t)=>s+Number(t.distance_miles||0),0);
            if (!vTrips.length) return null;
            return (
              <div key={v.imei} className="card" style={{marginBottom:16}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:12}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:15,color:NAVY}}>{v.nickname||v.model} — {v.year} {v.make}</div>
                    <div style={{fontSize:12,color:"#8993a4"}}>{v.technician_name||"Unassigned"} · {vTrips.length} trips</div>
                  </div>
                  <div style={{textAlign:isMobile?"left":"right"}}>
                    <div style={{fontSize:13,color:"#065F46",fontWeight:700}}>🏢 {bizMi.toFixed(1)} mi business</div>
                    <div style={{fontSize:13,color:"#6B7280"}}>🏠 {persMi.toFixed(1)} mi personal</div>
                    <div style={{fontSize:12,color:"#B45309",fontWeight:600}}>${(bizMi*irsRate).toFixed(2)} deductible</div>
                  </div>
                </div>

                {/* Trip list. Phone: stacked trip cards flowing into the page scroll — the
                    9-column table was a 320px 2-axis scroll trap with Map/Override ~450px off-screen. */}
                {isMobile&&(
                  <div>
                    {vTrips.map(t=>{
                      const st    = new Date(t.start_time);
                      const en    = new Date(t.end_time);
                      const dur   = Math.round((en-st)/60000);
                      const durFmt = dur>=60?`${Math.floor(dur/60)}h ${dur%60}m`:`${dur}m`;
                      const arrival    = t.arrival_time   ? new Date(t.arrival_time)   : null;
                      const departure  = t.departure_time ? new Date(t.departure_time) : null;
                      const atLocation = t.time_at_location_mins;
                      const atLocFmt   = atLocation!=null ? (atLocation>=60?`${Math.floor(atLocation/60)}h ${atLocation%60}m`:`${atLocation}m`) : null;
                      const biz = t.trip_type==="business";
                      const isSel = selectedTrip?.id===t.id;
                      return (
                        <div key={t.id} className="fleet-trip-mobile" style={{background:isSel?"#eff6ff":undefined,margin:isSel?"0 -8px":0,padding:isSel?"12px 8px":undefined,borderRadius:isSel?10:0}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--text)",minWidth:0}}>{st.toLocaleDateString()} · {st.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                            <span style={{fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:999,flexShrink:0,background:biz?"#dcfce7":"#f3f4f6",color:biz?"#065F46":"#6B7280"}}>
                              {biz?"🏢 Business":"🏠 Personal"}
                            </span>
                          </div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:"3px 12px",marginTop:5,fontSize:12,color:"var(--text2)"}}>
                            <span style={{fontWeight:600,color:"var(--text)"}}>{Number(t.distance_miles).toFixed(1)} mi</span>
                            <span>⏱ {durFmt}</span>
                            {t.override&&<span>✎ manual</span>}
                          </div>
                          {(t.matched_client_name||arrival||departure||atLocFmt)&&(
                            <div style={{display:"flex",flexWrap:"wrap",gap:"3px 12px",marginTop:4,fontSize:12,color:"#065F46"}}>
                              {t.matched_client_name&&<span style={{fontWeight:600}}>🐕 {t.matched_client_name}</span>}
                              {(arrival||departure)&&<span style={{color:"var(--text2)"}}>{arrival?arrival.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"} → {departure?departure.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"}</span>}
                              {atLocFmt&&<span style={{fontWeight:600,color:atLocation>0?"#065F46":"var(--text2)"}}>{atLocFmt} on site</span>}
                            </div>
                          )}
                          <div style={{display:"flex",gap:8,marginTop:10}}>
                            <button className="btn btn-ghost" style={{flex:1,justifyContent:"center",minHeight:40}} onClick={()=>fetchTripRoute(t)}>
                              {isSel?"✕ Hide map":"🗺 Map"}
                            </button>
                            <button className="btn btn-ghost" style={{flex:1,justifyContent:"center",minHeight:40}} onClick={()=>toggleTripType(t)}>
                              {t.override?"↩ Auto":(biz?"✎ Mark personal":"✎ Mark business")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {!isMobile&&(
                <div className="table-wrap" style={{maxHeight:320,overflowY:"auto"}}>
                  <table>
                    <thead><tr><th>Date</th><th>Duration</th><th>Miles</th><th>Client Visited</th><th>Arrival</th><th>Departure</th><th>At Location</th><th>Type</th><th></th></tr></thead>
                    <tbody>
                      {vTrips.map(t=>{
                        const st    = new Date(t.start_time);
                        const en    = new Date(t.end_time);
                        const dur   = Math.round((en-st)/60000);
                        const durFmt = dur>=60?`${Math.floor(dur/60)}h ${dur%60}m`:`${dur}m`;
                        // Arrival/departure from DB — source of truth
                        const arrival    = t.arrival_time   ? new Date(t.arrival_time)   : null;
                        const departure  = t.departure_time ? new Date(t.departure_time) : null;
                        const atLocation = t.time_at_location_mins;
                        const atLocFmt   = atLocation!=null ? (atLocation>=60?`${Math.floor(atLocation/60)}h ${atLocation%60}m`:`${atLocation}m`) : "—";
                        return (
                          <tr key={t.id} className="fleet-trow" style={{background:selectedTrip?.id===t.id?"#eff6ff":t.trip_type==="business"?"#f0fdf4":undefined}}>
                            <td style={{fontSize:12}}>{st.toLocaleDateString()} {st.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</td>
                            <td>{durFmt}</td>
                            <td style={{fontWeight:600}}>{Number(t.distance_miles).toFixed(1)} mi</td>
                            <td style={{fontSize:12,color:"#065F46"}}>{t.matched_client_name||"—"}</td>
                            <td style={{fontSize:11,color:"#42526e"}}>{arrival?arrival.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"}</td>
                            <td style={{fontSize:11,color:"#42526e"}}>{departure?departure.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"—"}</td>
                            <td style={{fontSize:11,fontWeight:600,color:atLocation!=null&&atLocation>0?"#065F46":"#8993a4"}}>{atLocFmt}</td>
                            <td>
                              <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:4,background:t.trip_type==="business"?"#dcfce7":"#f3f4f6",color:t.trip_type==="business"?"#065F46":"#6B7280"}}>
                                {t.trip_type==="business"?"🏢 Business":"🏠 Personal"}
                              </span>
                            </td>
                            <td style={{display:"flex",gap:4}}>
                              <button className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"2px 8px"}}
                                onClick={()=>fetchTripRoute(t)}>
                                {selectedTrip?.id===t.id?"✕ Hide":"🗺 Map"}
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"2px 8px"}}
                                onClick={()=>toggleTripType(t)}>
                                {t.override?"↩ Auto":"✎ Override"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}

                {/* Trip route map (desktop: inline under the table; phone: bottom sheet below) */}
                {!isMobile&&selectedTrip&&selectedTrip.imei===v.imei&&(
                  <div style={{marginTop:12}}>
                    <TripRouteMap trip={selectedTrip} route={tripRoute} relevantClientIds={dbTrips.filter(t=>t.matched_client_id).map(t=>t.matched_client_id)}/>
                  </div>
                )}
              </div>
            );
          })}
          {dbTrips.length===0&&<div className="empty"><p>No trips found for this date range. {isMobile?"Tap":"Click"} "Sync New Trips" to import.</p></div>}

          {/* Phone: the trip route opens as a bottom sheet over the list — inline it rendered
              below the table with nothing visibly changing on tap. Backdrop + × + back gesture close it. */}
          {isMobile&&selectedTrip&&(
            <>
              <div onClick={closeTrip} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:129,touchAction:"none"}}/>
              <div role="dialog" aria-label="Trip route" style={{position:"fixed",left:0,right:0,bottom:0,zIndex:130,background:"var(--surface)",borderRadius:"20px 20px 0 0",maxHeight:"85dvh",minHeight:0,overflowY:"auto",overscrollBehavior:"contain",boxShadow:"0 -8px 40px rgba(0,0,0,.28)",padding:"14px 14px calc(14px + env(safe-area-inset-bottom))"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,marginBottom:10}}>
                  <div style={{fontSize:15,fontWeight:600,color:"var(--text)",minWidth:0}}>🗺 Trip route{selectedTrip.matched_client_name?` · ${selectedTrip.matched_client_name}`:""}</div>
                  <button onClick={closeTrip} aria-label="Close" style={{width:36,height:36,flexShrink:0,borderRadius:"50%",border:"none",background:"var(--bg)",color:"var(--text2)",fontSize:20,lineHeight:1,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                </div>
                <TripRouteMap trip={selectedTrip} route={tripRoute} relevantClientIds={dbTrips.filter(t=>t.matched_client_id).map(t=>t.matched_client_id)}/>
                <button className="btn btn-ghost" style={{width:"100%",justifyContent:"center",minHeight:44,marginTop:12}} onClick={closeTrip}>Done</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Summary-bar stat cell (matches the Reports/Clients apple-native look).
// Phone (mobile + idx): the bar is a 2-column grid — hairline between the columns and
// above the second row, so 3 or 4 cells never leave an orphan with a stray right border.
function Stat({ label, value, tone, last, sub, mobile, idx = 0 }) {
  const accent = tone === "good" ? "#16a34a" : tone === "warn" ? "#B26B00" : tone === "gold" ? GOLD : NAVY;
  const line = "1px solid #e3e8ef";
  const box = mobile
    ? { minWidth: 0, padding: "12px 14px", borderRight: (idx % 2 === 0 && !last) ? line : "none", borderTop: idx >= 2 ? line : "none" }
    : { flex: "1 1 0", minWidth: 120, padding: "13px 16px", borderRight: last ? "none" : line };
  return (
    <div style={box}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "#8993a4" }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 700, color: accent, marginTop: 5 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#8993a4", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
